import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FileText, Download, CheckCircle, ArrowRight, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DocumentiIsee = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/documents/isee`);
        setData(response.data);
      } catch (error) {
        console.error("Error fetching ISEE documents:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-blue-900 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-sm font-semibold tracking-[0.1em] uppercase text-blue-300 mb-4 block">
              Documenti Necessari
            </span>
            <h1 className="font-heading text-4xl md:text-6xl font-black tracking-tighter text-white mb-6">
              {data?.title || "ISEE - DSU"}
            </h1>
            <p className="text-xl text-blue-100 leading-relaxed">
              Ecco l'elenco completo dei documenti necessari per la compilazione della DSU e il calcolo dell'ISEE {data?.year || "2026"}.
            </p>
          </div>
        </div>
      </section>

      {/* Info Banner */}
      <section className="bg-blue-50 border-b border-blue-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-blue-900 rounded-full flex items-center justify-center flex-shrink-0">
              <Info className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-blue-900 mb-1">Cosa serve per l'ISEE?</h3>
              <p className="text-blue-800">
                L'ISEE richiede informazioni su redditi e patrimonio di <strong>tutti i componenti</strong> del nucleo familiare riferite ai due anni precedenti. Per l'ISEE 2026, servono i dati del 2024.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Document Categories */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {data?.categories?.map((category, idx) => (
                <div 
                  key={idx}
                  className="bg-white border border-slate-200 rounded-2xl p-8"
                  data-testid={`isee-category-${idx}`}
                >
                  <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6 pb-4 border-b border-slate-200">
                    {category.name}
                  </h2>
                  <ul className="space-y-0">
                    {category.items.map((item, i) => (
                      <li 
                        key={i}
                        className="document-item flex items-start gap-3 py-4"
                      >
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-lg text-slate-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Quick Actions */}
                <div className="bg-slate-50 rounded-2xl p-6">
                  <h3 className="font-heading text-lg font-bold text-slate-900 mb-4">
                    Azioni Rapide
                  </h3>
                  <div className="space-y-3">
                    <Link to="/prenota" className="block">
                      <Button 
                        className="w-full bg-red-600 hover:bg-red-700 text-white rounded-full"
                        data-testid="isee-prenota"
                      >
                        Prenota Appuntamento
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                    <Button 
                      variant="outline" 
                      className="w-full rounded-full"
                      onClick={() => window.print()}
                      data-testid="isee-print"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Stampa Lista
                    </Button>
                  </div>
                </div>

                {/* Types of ISEE */}
                <div className="bg-slate-100 rounded-2xl p-6">
                  <h3 className="font-heading text-lg font-bold text-slate-900 mb-4">
                    Tipologie di ISEE
                  </h3>
                  <ul className="space-y-3 text-sm text-slate-700">
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-blue-900 rounded-full mt-2 flex-shrink-0" />
                      <span><strong>ISEE Ordinario</strong> - Per la maggior parte delle prestazioni</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-blue-900 rounded-full mt-2 flex-shrink-0" />
                      <span><strong>ISEE Universitario</strong> - Per borse di studio e tasse</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-blue-900 rounded-full mt-2 flex-shrink-0" />
                      <span><strong>ISEE Sociosanitario</strong> - Per prestazioni assistenziali</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-blue-900 rounded-full mt-2 flex-shrink-0" />
                      <span><strong>ISEE Minorenni</strong> - Per genitori non conviventi</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-blue-900 rounded-full mt-2 flex-shrink-0" />
                      <span><strong>ISEE Corrente</strong> - In caso di variazioni reddituali</span>
                    </li>
                  </ul>
                </div>

                {/* Help Box */}
                <div className="bg-blue-900 text-white rounded-2xl p-6">
                  <h3 className="font-heading text-lg font-bold mb-3">
                    Non sai quale ISEE ti serve?
                  </h3>
                  <p className="text-blue-100 text-sm mb-4">
                    Contattaci e ti aiuteremo a capire quale tipologia di ISEE è più adatta alle tue esigenze.
                  </p>
                  <Link to="/contatti">
                    <Button 
                      variant="outline" 
                      className="w-full bg-transparent border-white text-white hover:bg-white/10 rounded-full text-sm"
                      data-testid="isee-contatti"
                    >
                      Chiedi Informazioni
                    </Button>
                  </Link>
                </div>

                {/* 730 Link */}
                <div className="bg-slate-50 rounded-2xl p-6">
                  <h3 className="font-heading text-lg font-bold text-slate-900 mb-3">
                    Ti serve anche il 730?
                  </h3>
                  <p className="text-slate-600 text-sm mb-4">
                    Consulta anche la lista dei documenti per il Modello 730.
                  </p>
                  <Link to="/documenti-730">
                    <Button 
                      variant="link" 
                      className="p-0 h-auto text-blue-900 font-semibold"
                      data-testid="isee-730-link"
                    >
                      Documenti 730
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900 mb-4">
            Pronto per calcolare il tuo ISEE?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Porta tutti i documenti e il tuo ISEE sarà pronto in pochi minuti.
          </p>
          <Link to="/prenota">
            <Button 
              size="lg" 
              className="bg-red-600 hover:bg-red-700 text-white rounded-full px-8"
              data-testid="isee-cta-prenota"
            >
              Prenota Ora
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default DocumentiIsee;
